import React from 'react';
import { Calendar, RefreshCw, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

// Import hook for live data
import { useDashboardData } from '../hooks/useDashboardData';

// Import layout
import { Layout4Hybrid } from '../layouts/Layout4Hybrid';

// Import interfaces for type conversion
import { Offer, OfferElement } from '../interfaces/mock.interface';

const Index: React.FC = () => {
  // Use the custom hook for live data with 5-minute auto-refresh
  const { data, loading, refresh, lastUpdated } = useDashboardData(5 * 60 * 1000);

  // Convert LatestOfferResponse to Offer format expected by Layout4Hybrid
  const latestOffer: Offer | null = data.latestOffer
    ? {
        id: data.latestOffer.id,
        type: data.latestOffer.type,
        email: '',
        clientId: 0,
        validityStartDate: data.latestOffer.validityStartDate,
        validityEndDate: data.latestOffer.validityEndDate,
        status: data.latestOffer.status,
        terminal: data.latestOffer.terminal,
        isTemplate: false,
        containers: [],
        offerElements: data.latestOffer.offerElements.map((el): OfferElement => ({
          id: el.id,
          offerId: data.latestOffer!.id,
          containerType: el.containerType,
          currency: el.currency,
          price: el.price,
          validFrom: el.validFrom,
          validTo: el.validTo,
        })),
        clientName: data.latestOffer.clientName,
      }
    : null;

  const handleRefresh = async () => {
    await refresh();
  };

  // Loading skeleton for dashboard
  if (loading && !lastUpdated) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-0 pt-2">
          {/* Header */}
          <div className="flex items-center justify-between z-10 pb-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
            </div>
            <div className="flex items-center gap-4">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-9 w-32" />
            </div>
          </div>

          {/* Loading skeletons for widgets */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Skeleton className="h-64 col-span-2" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64 col-span-2" />
            <Skeleton className="h-64 col-span-2" />
            <Skeleton className="h-64" />
            <Skeleton className="h-48 col-span-3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-0 pt-2">
        {/* Header */}
        <div className="flex items-center justify-between z-10 pb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-0">
              <Calendar className="mr-1 h-3 w-3" />
              Actualizat: {lastUpdated ? lastUpdated.toLocaleString('ro-RO') : new Date().toLocaleDateString('ro-RO')}
            </Badge>
            <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Reîmprospătare
            </Button>
          </div>
        </div>

        {/* Dashboard Content */}
        <Layout4Hybrid
          completedContainers={data.completedContainers}
          vesselDepartures={data.vesselDepartures}
          portTransitTimes={data.portTransitTimes}
          currentPrices={data.currentPrices}
          latestOffer={latestOffer}
        />
      </div>
    </div>
  );
};

export default Index;
